import { ReactNode, useEffect, useState } from "react"
import PlanBadge from "src/components/premium/PlanBadge"
import AccountAvatar from "src/components/account/AccountAvatar"
import navigationService from "src/navigation/NavigationService"
import StateConstants from "src/states/StateContstants"
import UserPermissionHelper from "src/utils/UserPermissionHelper"
import "./Search.css"

interface LandingChromeProps {
    stateHandler: any
    loginHelper?: any
    children: ReactNode
}

/**
 * Shared chrome (emerald top bar + footer) for the primary content pages
 * such as Terms, Saved and area pages. Hero pages (landing, area search) keep
 * their own hero-embedded nav.
 */
export default function LandingChrome({ stateHandler, children }: LandingChromeProps) {
    const blogIndexPath = "/blog/"
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(!!stateHandler.get(StateConstants.IS_LOGGED_IN))
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)
    const [canViewAnalysis, setCanViewAnalysis] = useState<boolean>(
        UserPermissionHelper.canViewAnalysisDashboard(stateHandler))

    useEffect(() => {
        let mounted = true
        stateHandler.addListener(StateConstants.IS_LOGGED_IN, (value: boolean) => {
            if (mounted) setIsLoggedIn(!!value)
        })
        stateHandler.addListener(StateConstants.USER_PERMISSIONS, () => {
            if (mounted) setCanViewAnalysis(UserPermissionHelper.canViewAnalysisDashboard(stateHandler))
        })
        return () => { mounted = false }
    }, [stateHandler])

    const nav = (path: string) => { setIsMenuOpen(false); navigationService.navigateTo(path) }
    const openPlans = () => { setIsMenuOpen(false); stateHandler.set(StateConstants.SHOW_PLAN_MODAL, true) }
    const openLogin = () => {
        setIsMenuOpen(false)
        stateHandler.set(StateConstants.LOGIN_MODAL_MODE, "login")
        stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
    }
    const openSignUp = () => {
        setIsMenuOpen(false)
        stateHandler.set(StateConstants.LOGIN_MODAL_MODE, "signup")
        stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
    }

    const brand = (asLink: boolean) => {
        const content = (
            <>
                <span className="landing-brand-mark" aria-hidden>
                    <img src="/homeindex_logo.svg" alt="HomeIndex" />
                </span>
                <span className="landing-brand-text">HomeIndex.ie</span>
            </>
        )
        return asLink
            ? <a href="/" className="landing-brand" aria-label="HomeIndex - Home">{content}</a>
            : <span className="landing-brand">{content}</span>
    }

    return (
        <div className="landing-new-view landing-chrome">
            <header className="landing-topbar">
                <div className={"landing-nav" + (isMenuOpen ? " landing-nav-open" : "")}>
                    {brand(true)}
                    <button
                        type="button"
                        className="landing-nav-toggle"
                        aria-label="Toggle menu"
                        aria-expanded={isMenuOpen}
                        onClick={() => setIsMenuOpen(v => !v)}
                    >
                        <span aria-hidden>{isMenuOpen ? "✕" : "☰"}</span>
                    </button>
                    <nav className="landing-nav-links" aria-label="Primary">
                        <button type="button" onClick={() => nav("/")}>Valuations</button>
                        <button type="button" onClick={() => nav("/reports")}>Reports</button>
                        <a href={blogIndexPath} onClick={() => setIsMenuOpen(false)}>Blog</a>
                        {canViewAnalysis && (
                            <button type="button" className="landing-nav-analysis" onClick={() => nav("/analysis")}>
                                Analysis
                            </button>
                        )}
                    </nav>
                    <div className="landing-auth-actions">
                        {!isLoggedIn && (
                            <>
                                <button className="landing-signup-button" type="button" onClick={openSignUp}>Sign up</button>
                                <button className="landing-login-button" type="button" onClick={openLogin}>Log in</button>
                            </>
                        )}
                        {isLoggedIn && (
                            <PlanBadge stateHandler={stateHandler} />
                        )}
                        {isLoggedIn && (
                            <AccountAvatar stateHandler={stateHandler} onClick={() => nav("/account")} />
                        )}
                    </div>
                </div>
            </header>

            {children}

            <footer className="landing-footer">
                <div className="landing-footer-brand">
                    {brand(false)}
                    <p>Property intelligence for buyers and sellers.<br />Local knowledge. Smarter decisions.</p>
                    <div className="landing-social-row" aria-label="Social links">
                        <a href="https://www.facebook.com/" aria-label="Facebook">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9.198 21.5h4v-8.01h3.604l.396-3.98h-4V7.5a1 1 0 011-1h3v-4h-3a5 5 0 00-5 5v2.01h-2l-.396 3.98h2.396v8.01z"/></svg>
                        </a>
                        <a href="https://twitter.com/" aria-label="X">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23.643 4.937a9.57 9.57 0 01-2.828.775 4.889 4.889 0 002.165-2.724 9.664 9.664 0 01-3.127 1.195A4.916 4.916 0 0016.616 3c-2.737 0-4.952 2.235-4.952 4.99 0 .39.045.77.127 1.134C7.728 8.89 4.1 6.89 1.671 3.905a5.048 5.048 0 00-.67 2.508c0 1.732.878 3.26 2.212 4.154a4.904 4.904 0 01-2.244-.622v.063c0 2.418 1.712 4.434 3.983 4.892a4.935 4.935 0 01-2.237.085c.63 1.972 2.457 3.408 4.624 3.448A9.868 9.868 0 010 20.544a13.94 13.94 0 007.548 2.212c9.057 0 14.01-7.514 14.01-14.024 0-.213-.005-.426-.015-.637A10.025 10.025 0 0024 4.59a9.722 9.722 0 01-2.357.346z"/></svg>
                        </a>
                        <a href="https://www.linkedin.com/" aria-label="LinkedIn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        </a>
                        <a href="https://www.instagram.com/" aria-label="Instagram">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                        </a>
                    </div>
                </div>
                <div className="landing-footer-links">
                    <div>
                        <strong>Product</strong>
                        <button type="button" onClick={() => nav("/")}>Valuations</button>
                        <button type="button" onClick={() => nav("/areas")}>Areas</button>
                        <button type="button" onClick={openPlans}>Reports</button>
                        <button type="button" onClick={openPlans}>Pricing</button>
                    </div>
                    <div>
                        <strong>Resources</strong>
                        <a href={blogIndexPath}>Blog</a>
                        <a href={blogIndexPath}>Market Insights</a>
                        <a href={blogIndexPath}>Buying Guide</a>
                        <button type="button" onClick={() => nav("/contact")}>Help Centre</button>
                        <a href={blogIndexPath}>Glossary</a>
                    </div>
                    <div>
                        <strong>Company</strong>
                        <button type="button" onClick={() => nav("/contact")}>About Us</button>
                        <button type="button" onClick={() => nav("/contact")}>Contact Us</button>
                    </div>
                    <div>
                        <strong>Legal</strong>
                        <button type="button" onClick={() => nav("/terms")}>Terms of Use</button>
                        <button type="button" onClick={() => nav("/terms")}>Privacy Policy</button>
                        <button type="button" onClick={() => nav("/terms")}>Cookie Policy</button>
                        <button type="button" onClick={() => nav("/terms")}>Data Protection</button>
                    </div>
                </div>
                <div className="landing-footer-bottom">
                    <span>© 2026 HomeIndex. All rights reserved.</span>
                    <span><span className="landing-proudly">Proudly Irish</span> <span className="irish-flag" aria-hidden /></span>
                </div>
            </footer>
        </div>
    )
}
